
     


import React, { useState, useEffect, useCallback, useMemo, Component, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../components/api/api-base";
import {
  Card,
  Button,
  Form,
  Input,
  message,
  Row,
  Col,
  Table,
  Dropdown,
  Menu,
  Modal,
  Select,
  DatePicker,
  InputNumber,
  Spin,
  Tooltip,
  Image,
  Checkbox,
  Upload,
  Typography,
  Progress,
  Space
} from "antd";
import {
  EditOutlined,
  EllipsisOutlined,
  DeleteOutlined,
  DollarCircleFilled,
  SearchOutlined,
  EyeOutlined,
  PlusOutlined,
  DownloadOutlined,
  UploadOutlined,
  SaveOutlined,
  CloseOutlined,
  DeleteRowOutlined,
} from "@ant-design/icons";
import { useBusinessContext } from "../../components/BusinessContext";
import { useAuthContext } from "../../components/AuthProvider";
import moment from "moment";
import * as XLSX from "xlsx";
import { Archive } from "feather-icons-react";
const { Option } = Select;
const { Title, Text } = Typography;

const API_PAGE_SIZE = 100;
const BACKGROUND_FETCH_CONCURRENCY = 3;
const SHOP_PRODUCTS_CACHE_PREFIX = "shop_details_products_v2_";
const SHOP_PRODUCTS_CACHE_DURATION = 60 * 60 * 1000;

const buildProductDetailsEntry = (shopProduct) => {
  if (!shopProduct?.product || !shopProduct?.productId) return null;
  const product = shopProduct.product;
  return {
    id: product.id || shopProduct.productId,
    name: product.name || "",
    code: product.code || "",
    barCode: product.barCode || "",
    description: product.description || "",
    type: product.type || "",
    brand: product.brand || "",
    ingredients: product.ingredients || "",
    baseUnitOfMeasure: product.baseUnitOfMeasure || "Unit",
    grossWeight: product.grossWeight || 0,
    netWeight: product.netWeight || 0,
    generalPrice: product.generalPrice || 0,
    applyGeneralPriceToShopProducts: product.applyGeneralPriceToShopProducts ?? true,
    active: product.active ?? true,
    images: Array.isArray(product.images) ? product.images : [],
    specifications: Array.isArray(product.specifications) ? product.specifications : [],
  };
};

const mergeProductDetails = (products) => {
  const productDetailsData = {};
  products.forEach((shopProduct) => {
    const entry = buildProductDetailsEntry(shopProduct);
    if (entry) productDetailsData[shopProduct.productId] = entry;
  });
  return productDetailsData;
};

const getProductInfo = (shopProduct, productDetails) => {
  const details = productDetails[shopProduct?.productId] || {};
  const nested = shopProduct?.product || {};
  return {
    name: details.name || nested.name || "",
    code: details.code || nested.code || "",
    barCode: details.barCode || nested.barCode || "",
    description: details.description || nested.description || "",
    images: details.images?.length ? details.images : nested.images || [],
  };
};

const filterShopProducts = (products, searchTerm, filterType, productDetails) => {
  let filtered = products;

  if (searchTerm.trim()) {
    const q = searchTerm.trim().toLowerCase();
    filtered = filtered.filter((shopProduct) => {
      const info = getProductInfo(shopProduct, productDetails);
      return (
        info.name.toLowerCase().includes(q) ||
        info.code.toLowerCase().includes(q) ||
        info.barCode.toLowerCase().includes(q) ||
        info.description.toLowerCase().includes(q)
      );
    });
  }

  switch (filterType) {
    case "withQuantity":
      filtered = filtered.filter((p) => (p?.totalQuantityOnHandAtShopDisplays || 0) > 0);
      break;
    case "withoutQuantity":
      filtered = filtered.filter((p) => (p?.totalQuantityOnHandAtShopDisplays || 0) === 0);
      break;
    case "withBarcode": {
      filtered = filtered.filter((p) => {
        const info = getProductInfo(p, productDetails);
        return Boolean(info.barCode);
      });
      break;
    }
    case "withoutBarcode": {
      filtered = filtered.filter((p) => {
        const info = getProductInfo(p, productDetails);
        return !info.barCode;
      });
      break;
    }
    case "withoutImages": {
      filtered = filtered.filter((p) => {
        const info = getProductInfo(p, productDetails);
        return !info.images?.length;
      });
      break;
    }
    case "withPrice":
      filtered = filtered.filter((p) => p?.currentRetailShopProductPriceToApply);
      break;
    case "withoutPrice":
      filtered = filtered.filter((p) => !p?.currentRetailShopProductPriceToApply);
      break;
    default:
      break;
  }

  return filtered;
};

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "20px", color: "red" }}>
          <h1>Something went wrong.</h1>
          <p>{this.state.error?.message || "Unknown error occurred"}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ShopCardDetails() {
  const location = useLocation();
  const shopId = location.state?.shopId || null;
  const navigate = useNavigate();
  const { businessName = "", businessId = null } = useBusinessContext() || {};
  const { token = null } = useAuthContext() || {};
  
  // State declarations
  const [shopProducts, setShopProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [catalogLoadProgress, setCatalogLoadProgress] = useState(0);
  const [productDetails, setProductDetails] = useState({});
  const [shopDisplays, setShopDisplays] = useState([]);
  const [shopWarehouses, setShopWarehouses] = useState([]);
  const [shop, setShop] = useState(null);
  const [targetCustomers, setTargetCustomers] = useState([]);
  const [pricePurposes, setPricePurposes] = useState([]);
  const [editing, setEditing] = useState(false);
  const [priceModalVisible, setPriceModalVisible] = useState(false);
  const [editProductModalVisible, setEditProductModalVisible] = useState(false);
  const [priceDetailsVisible, setPriceDetailsVisible] = useState(false);
  const [warehouseDetailsVisible, setWarehouseDetailsVisible] = useState(false);
  const [addQuantityModalVisible, setAddQuantityModalVisible] = useState(false);
  const [bulkPriceModalVisible, setBulkPriceModalVisible] = useState(false);
  const [bulkSearchModalVisible, setBulkSearchModalVisible] = useState(false);
  const [bulkRemoveModalVisible, setBulkRemoveModalVisible] = useState(false);
  const [bulkActionsModalVisible, setBulkActionsModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [segments, setSegments] = useState({});
  const [availableQuantity, setAvailableQuantity] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [showFullName, setShowFullName] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [progress, setProgress] = useState(0);
  const [barcodeFilterList, setBarcodeFilterList] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Refs
  const fetchInProgress = useRef(false);
  const initialFetchDone = useRef(false);
  const backgroundFetchIdRef = useRef(0);
  const shopProductsRef = useRef([]);
  
  // Forms
  const [form] = Form.useForm();
  const [priceForm] = Form.useForm();
  const [editProductForm] = Form.useForm();
  const [addQuantityForm] = Form.useForm();
  const [bulkPriceForm] = Form.useForm();
  const [bulkSearchForm] = Form.useForm();
  const [bulkRemoveForm] = Form.useForm();

  // Function to download bulk search template
  const handleDownload = () => {
    try {
      const data = [{ BarCode: '' }];
      const worksheet = XLSX.utils.json_to_sheet(data, {
        header: ['BarCode'],
        skipHeader: false,
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
      XLSX.writeFile(workbook, 'bulk_search_template.xlsx');
      message.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error generating Excel template:', error);
      message.error(`Failed to download template: ${error.message}`);
    }
  };

  const syncShopProducts = useCallback((products, totalCount) => {
    shopProductsRef.current = products;
    setShopProducts(products);
    setTotalProducts(totalCount || products.length);
    setProductDetails((prev) => ({ ...prev, ...mergeProductDetails(products) }));
  }, []);

  const readShopProductsCache = useCallback(() => {
    if (!shopId) return null;
    try {
      const raw = localStorage.getItem(`${SHOP_PRODUCTS_CACHE_PREFIX}${shopId}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.products?.length) return null;
      if (Date.now() - parsed.timestamp >= SHOP_PRODUCTS_CACHE_DURATION) return null;
      return parsed.products;
    } catch {
      return null;
    }
  }, [shopId]);

  const writeShopProductsCache = useCallback(
    (products) => {
      if (!shopId || !products?.length) return;
      const payload = JSON.stringify({ products, timestamp: Date.now() });
      const write = () => {
        try {
          localStorage.setItem(`${SHOP_PRODUCTS_CACHE_PREFIX}${shopId}`, payload);
        } catch {
          // optional cache
        }
      };
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        window.requestIdleCallback(write, { timeout: 2000 });
      } else {
        setTimeout(write, 0);
      }
    },
    [shopId]
  );

  const loadRemainingProductPages = useCallback(
    async (fetchId, totalCount) => {
      const totalPages = Math.ceil(totalCount / API_PAGE_SIZE);
      if (totalPages <= 1) {
        setIsLoadingCatalog(false);
        setCatalogLoadProgress(100);
        writeShopProductsCache(shopProductsRef.current);
        return;
      }

      setIsLoadingCatalog(true);
      let merged = [...shopProductsRef.current];

      const fetchPage = async (page) => {
        const response = await api.get(
          `/shopproducts/shop/${shopId}?page=${page}&pageSize=${API_PAGE_SIZE}&fullyPaginated=true`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data?.data || [];
      };

      try {
        for (let start = 2; start <= totalPages; start += BACKGROUND_FETCH_CONCURRENCY) {
          if (backgroundFetchIdRef.current !== fetchId) return;

          const pages = [];
          for (
            let page = start;
            page < start + BACKGROUND_FETCH_CONCURRENCY && page <= totalPages;
            page += 1
          ) {
            pages.push(page);
          }

          const batches = await Promise.all(pages.map((page) => fetchPage(page)));
          const batch = batches.flat();
          if (!batch.length) break;

          merged = [...merged, ...batch];
          shopProductsRef.current = merged;
          setShopProducts(merged);
          setProductDetails((prev) => ({ ...prev, ...mergeProductDetails(batch) }));
          setCatalogLoadProgress(Math.min(99, Math.round((merged.length / totalCount) * 100)));
        }

        if (backgroundFetchIdRef.current === fetchId) {
          writeShopProductsCache(merged);
        }
      } catch (error) {
        console.error("Background shop products fetch error:", error);
      } finally {
        if (backgroundFetchIdRef.current === fetchId) {
          setIsLoadingCatalog(false);
          setCatalogLoadProgress(100);
        }
      }
    },
    [shopId, token, writeShopProductsCache]
  );

  const loadProductCatalog = useCallback(
    async (force = false) => {
      if (!shopId || !token || fetchInProgress.current) return;

      backgroundFetchIdRef.current += 1;
      const fetchId = backgroundFetchIdRef.current;
      fetchInProgress.current = true;

      const cachedProducts = !force ? readShopProductsCache() : null;

      const fetchFromNetwork = async () => {
        try {
          const response = await api.get(
            `/shopproducts/shop/${shopId}?page=1&pageSize=${API_PAGE_SIZE}&fullyPaginated=true`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const initial = response.data?.data || [];
          const totalCount =
            response.data?.totalCount ||
            response.data?.pagination?.totalCount ||
            initial.length;

          if (!initial.length) {
            syncShopProducts([], 0);
            return;
          }

          syncShopProducts(initial, totalCount);
          setCatalogLoadProgress(Math.min(99, Math.round((initial.length / totalCount) * 100)));
          writeShopProductsCache(initial);
          await loadRemainingProductPages(fetchId, totalCount);
        } catch (error) {
          console.error("Error fetching shop products:", error);
          if (!cachedProducts?.length) {
            message.error(`Failed to load products: ${error.message}`);
          } else {
            message.warning("Using cached products due to a network error.");
          }
        } finally {
          setProductsLoading(false);
          fetchInProgress.current = false;
        }
      };

      if (cachedProducts?.length) {
        syncShopProducts(cachedProducts, cachedProducts.length);
        setCatalogLoadProgress(100);
        setIsLoadingCatalog(false);
        setProductsLoading(false);
        fetchInProgress.current = false;
        fetchFromNetwork();
        return;
      }

      setProductsLoading(true);
      setIsLoadingCatalog(false);
      setCatalogLoadProgress(0);
      await fetchFromNetwork();
    },
    [
      shopId,
      token,
      readShopProductsCache,
      syncShopProducts,
      writeShopProductsCache,
      loadRemainingProductPages,
    ]
  );

  const refreshCatalog = useCallback(
    (force = true) => loadProductCatalog(force),
    [loadProductCatalog]
  );
  // Initial data fetch
  // useEffect(() => {
  //   const fetchInitialData = async () => {
  //     if (!shopId || !businessId || !token || initialFetchDone.current) {
  //       return;
  //     }
      
  //     setLoading(true);
  //     initialFetchDone.current = true;
      
  //     try {
  //       // Fetch shop details
  //       const shopResponse = await api.get(`/shops/${shopId}`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       });
  //       const shopData = shopResponse.data || {};
  //       setShop(shopData);
  //       form.setFieldsValue(shopData);
        
  //       // Fetch shop displays
  //       const shopDisplaysResponse = await api.get(`/shopdisplays/shop/${shopId}`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       });
  //       setShopDisplays(Array.isArray(shopDisplaysResponse.data) ? shopDisplaysResponse.data : []);
        
  //       // Fetch shop warehouses
  //       const shopWarehousesResponse = await api.get(`/shopwarehouses/shop/${shopId}`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       });
  //       setShopWarehouses(Array.isArray(shopWarehousesResponse.data) ? shopWarehousesResponse.data : []);
        
  //       // Fetch warehouses
  //       const warehouseResponse = await api.get(`/warehouses/business/${businessId}`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       });
  //       const warehouseData = Array.isArray(warehouseResponse.data) ? warehouseResponse.data : [];
  //       setWarehouses(warehouseData);
        
  //       // Initialize segments structure
  //       const initialSegments = warehouseData.reduce((acc, warehouse) => ({
  //         ...acc,
  //         [warehouse.id]: [],
  //       }), {});
  //       setSegments(initialSegments);
        
  //       // Fetch target customers
  //       const targetCustomersResponse = await api.get(`/shopproductprices/target-customers`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       });
  //       setTargetCustomers(Array.isArray(targetCustomersResponse.data) ? targetCustomersResponse.data : []);
        
  //       // Fetch price purposes
  //       const purposesResponse = await api.get(`/shopproductprices/shop-product-price-purposes`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       });
  //       setPricePurposes(Array.isArray(purposesResponse.data) ? purposesResponse.data : ["Retail", "Wholesale", "Promotion"]);
        
  //       // Fetch initial products
  //       await fetchShopProducts(1, pageSize, true);
        
  //     } catch (error) {
  //       console.error('Error fetching initial data:', error);
  //       message.error(`Failed to fetch shop data: ${error.message}`);
  //       navigate("/shops");
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
    
  //   fetchInitialData();
    
  //   return () => {
  //     initialFetchDone.current = false;
  //   };
  // }, [shopId, businessId, token, navigate, form, fetchShopProducts, pageSize]);

  useEffect(() => {
  const fetchInitialData = async () => {
    if (!shopId || !businessId || !token || initialFetchDone.current) {
      return;
    }
    
    setLoading(true);
    initialFetchDone.current = true;
    
    try {
      // Fetch shop details
      const shopResponse = await api.get(`/shops/${shopId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const shopData = shopResponse.data || {};
      setShop(shopData);
      form.setFieldsValue(shopData);
      
      // Fetch shop displays
      const shopDisplaysResponse = await api.get(`/shopdisplays/shop/${shopId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShopDisplays(Array.isArray(shopDisplaysResponse.data) ? shopDisplaysResponse.data : []);
      
      // Fetch shop warehouses
      const shopWarehousesResponse = await api.get(`/shopwarehouses/shop/${shopId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShopWarehouses(Array.isArray(shopWarehousesResponse.data) ? shopWarehousesResponse.data : []);
      
      // Fetch warehouses
      const warehouseResponse = await api.get(`/warehouses/business/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const warehouseData = Array.isArray(warehouseResponse.data) ? warehouseResponse.data : [];
      setWarehouses(warehouseData);
      
      // Initialize segments structure
      const initialSegments = warehouseData.reduce((acc, warehouse) => ({
        ...acc,
        [warehouse.id]: [],
      }), {});
      setSegments(initialSegments);
      
      // Fetch target customers
      const targetCustomersResponse = await api.get(`/shopproductprices/target-customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTargetCustomers(Array.isArray(targetCustomersResponse.data) ? targetCustomersResponse.data : []);
      
      // Fetch price purposes
      const purposesResponse = await api.get(`/shopproductprices/shop-product-price-purposes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPricePurposes(Array.isArray(purposesResponse.data) ? purposesResponse.data : ["Retail", "Wholesale", "Promotion"]);
      
      await loadProductCatalog(false);
      
    } catch (error) {
      console.error('Error fetching initial data:', error);
      message.error(`Failed to fetch shop data: ${error.message}`);
      navigate("/shops");
    } finally {
      setLoading(false);
    }
  };
  
  fetchInitialData();
  
  // Don't reset initialFetchDone in cleanup - we only want to fetch once per component mount
  return () => {
    // Optional cleanup if needed
  };
}, [shopId, businessId, token, navigate, form, loadProductCatalog]);

  const filteredShopProducts = useMemo(() => {
    let result = filterShopProducts(shopProducts, searchTerm, filterType, productDetails);
    if (barcodeFilterList?.length) {
      const allow = new Set(barcodeFilterList.map((b) => String(b).trim()));
      result = result.filter((product) => {
        const barCode = getProductInfo(product, productDetails).barCode?.toString().trim();
        return barCode && allow.has(barCode);
      });
    }
    return result;
  }, [shopProducts, searchTerm, filterType, productDetails, barcodeFilterList]);

  const paginatedShopProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredShopProducts.slice(start, start + pageSize);
  }, [filteredShopProducts, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);

  // Edit product submit handler
  const handleEditProductSubmit = useCallback(async () => {
    if (!selectedProduct?.productId) {
      message.error("No product selected");
      return;
    }
    
    setModalLoading(true);
    try {
      const values = await editProductForm.validateFields();
      const productId = productDetails[selectedProduct.productId]?.id;
      
      if (!productId) {
        throw new Error("Product ID not found");
      }
      
      const payload = {
        id: productId,
        barCode: values.barCode,
        type: values.type,
        name: values.name,
        description: values.description,
        brand: values.brand,
        ingredients: values.ingredients,
        baseUnitOfMeasure: values.baseUnitOfMeasure,
        grossWeight: parseFloat(values.grossWeight) || 0,
        netWeight: parseFloat(values.netWeight) || 0,
        generalPrice: parseFloat(values.generalPrice) || 0,
        applyGeneralPriceToShopProducts: values.applyGeneralPriceToShopProducts ?? false,
        active: values.active,
        images: values.imageFilePath
          ? [
              {
                filePath: values.imageFilePath,
                title: values.imageTitle || "",
                description: values.imageDescription || "",
              },
            ]
          : [],
        specifications: productDetails[selectedProduct.productId]?.specifications || [],
      };
      
      await api.put(`/products/${productId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Update product details locally
      setProductDetails(prev => ({
        ...prev,
        [selectedProduct.productId]: {
          ...prev[selectedProduct.productId],
          ...payload
        }
      }));
      
      message.success("Product updated successfully");
      setEditProductModalVisible(false);
      editProductForm.resetFields();
      setSelectedProduct(null);
      
      // Refresh the current page
      await refreshCatalog(true);
      
    } catch (error) {
      console.error('Error updating product:', error);
      message.error(`Failed to update product: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  }, [editProductForm, selectedProduct, productDetails, token, refreshCatalog]);

  // Price submit handler
  const handlePriceSubmit = useCallback(async () => {
    if (!selectedProduct?.id) {
      message.error("No product selected");
      return;
    }
    
    try {
      const values = await priceForm.validateFields();
      const payload = {
        shopProductId: selectedProduct.id,
        price: parseFloat(values.price),
        purpose: values.purpose,
        targetCustomer: values.targetCustomer,
        effectiveDate: values.effectiveDate?.toISOString(),
        endDate: values.endDate?.toISOString(),
        active: values.active ?? true,
      };
      
      if (selectedPrice?.id) {
        payload.id = selectedPrice.id;
        await api.put(`/shopproductprices/${selectedPrice.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Product price updated successfully");
      } else {
        await api.post(`/shopproductprices`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Product price added successfully");
      }
      
      setPriceModalVisible(false);
      priceForm.resetFields();
      setSelectedPrice(null);
      
      await refreshCatalog(true);
      
    } catch (error) {
      console.error('Error saving price:', error);
      message.error(`Failed to save product price: ${error.message}`);
    }
  }, [priceForm, selectedProduct, selectedPrice, token, refreshCatalog]);

  // Bulk price update handler
  const handleBulkPriceUpdate = useCallback(async () => {
    try {
      const values = await bulkPriceForm.validateFields();
      const file = values.file.file;
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(sheet);
          
          if (!jsonData.length) {
            message.error("Excel file is empty");
            return;
          }
          
          const requiredFields = ["Product ID", "Price"];
          const missingFields = requiredFields.filter(
            (field) => !Object.keys(jsonData[0]).includes(field)
          );
          
          if (missingFields.length) {
            message.error(`Missing required fields in Excel: ${missingFields.join(", ")}`);
            return;
          }
          
          setModalLoading(true);
          
          const promises = jsonData.map(async (row) => {
            const shopProduct = shopProducts.find((p) => p.productId === row["Product ID"]);
            if (!shopProduct) {
              console.warn(`Product with ID ${row["Product ID"]} not found`);
              return null;
            }
            
            const payload = {
              shopProductId: shopProduct.id,
              price: parseFloat(row["Price"]),
              purpose: row["Purpose"] || "Retail",
              targetCustomer: row["Target Customer"] || "General Public",
              effectiveDate: row["Effective Date"]
                ? moment(row["Effective Date"]).toISOString()
                : moment().toISOString(),
              endDate: row["End Date"] ? moment(row["End Date"]).toISOString() : null,
              active: row["Active"] !== undefined ? !!row["Active"] : true,
            };
            
            const existingPrice = shopProduct.currentRetailShopProductPriceToApply;
            if (existingPrice?.id) {
              payload.id = existingPrice.id;
              return api.put(`/shopproductprices/${existingPrice.id}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
              });
            } else {
              return api.post(`/shopproductprices`, payload, {
                headers: { Authorization: `Bearer ${token}` },
              });
            }
          });
          
          await Promise.all(promises.filter(p => p !== null));
          message.success("Bulk price update completed successfully");
          setBulkPriceModalVisible(false);
          setBulkActionsModalVisible(false);
          bulkPriceForm.resetFields();
          
          // Refresh data
          await refreshCatalog(true);
          
        } catch (error) {
          console.error('Error in bulk price update:', error);
          message.error(`Failed to process bulk price update: ${error.message}`);
        } finally {
          setModalLoading(false);
        }
      };
      
      reader.readAsArrayBuffer(file);
      
    } catch (error) {
      console.error('Error validating bulk price form:', error);
      message.error(`Failed to validate bulk price form: ${error.message}`);
    }
  }, [bulkPriceForm, shopProducts, token, refreshCatalog]);

  // Add quantity handler
  const handleAddQuantity = useCallback(async () => {
    if (!selectedProduct?.productId) {
      message.error("No product selected");
      return;
    }
    
    setModalLoading(true);
    try {
      const values = await addQuantityForm.validateFields();
      const payload = {
        warehouseId: values.warehouseId,
        sourceSegmentId: values.sourceSegmentId,
        shopDisplayId: values.shopDisplayId,
        productId: selectedProduct.productId,
        quantity: values.quantity,
        unitOfMeasure: selectedProduct.product?.baseUnitOfMeasure || "Unit",
      };
      
      const response = await api.post(`/shopdisplayinventories/transfer-from-warehouse`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.status === 204 || response.status === 200) {
        message.success("Quantity added successfully");
        setAddQuantityModalVisible(false);
        addQuantityForm.resetFields();
        setSelectedProduct(null);
        setAvailableQuantity(null);
        
        // Refresh current page
        await refreshCatalog(true);
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error('Error adding quantity:', error);
      message.error(`Failed to add quantity: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  }, [addQuantityForm, selectedProduct, token, refreshCatalog]);

  // Delete product handler
  const handleDelete = useCallback(async (productId) => {
    try {
      await api.delete(`/shopproducts/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setShopProducts((prev) => {
        const next = prev.filter((p) => p.id !== productId);
        shopProductsRef.current = next;
        return next;
      });
      setSelectedRowKeys((prev) => prev.filter((key) => key !== productId));
      
      message.success("Product deleted successfully");
    } catch (error) {
      console.error('Error deleting product:', error);
      message.error(`Failed to delete product: ${error.message}`);
    }
  }, [token]);

  // Bulk remove handler
  const handleBulkRemove = useCallback(async () => {
    if (!selectedRowKeys.length) {
      message.warning("Please select at least one product to remove");
      return;
    }
    
    setModalLoading(true);
    try {
      const promises = selectedRowKeys.map((productId) =>
        api.delete(`/shopproducts/${productId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      );
      
      await Promise.all(promises);
      
      // Update local state
      setShopProducts((prev) => {
        const next = prev.filter((p) => !selectedRowKeys.includes(p.id));
        shopProductsRef.current = next;
        return next;
      });
      setSelectedRowKeys([]);
      
      message.success("Selected products removed successfully");
      setBulkActionsModalVisible(false);
    } catch (error) {
      console.error('Error in bulk remove:', error);
      message.error(`Failed to remove selected products: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  }, [selectedRowKeys, token]);

  // Bulk remove from file handler
  const handleBulkRemoveFromFile = useCallback(async () => {
    try {
      const values = await bulkRemoveForm.validateFields();
      const file = values.file.file;
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(sheet);
          
          if (!jsonData.length) {
            message.error("Excel file is empty");
            return;
          }
          
          const requiredFields = ["Product ID"];
          const missingFields = requiredFields.filter(
            (field) => !Object.keys(jsonData[0]).includes(field)
          );
          
          if (missingFields.length) {
            message.error(`Missing required field in Excel: ${missingFields.join(", ")}`);
            return;
          }
          
          const productIds = jsonData
            .map((row) => row["Product ID"]?.toString().trim())
            .filter((id) => id);
          
          if (!productIds.length) {
            message.error("No valid product IDs found in the Excel file");
            return;
          }
          
          setModalLoading(true);
          
          // Filter valid product IDs
          const validProductIds = shopProducts
            .filter((product) => productIds.includes(product.id.toString()))
            .map((product) => product.id);
          
          if (!validProductIds.length) {
            message.warning("No valid product IDs found in the shop's product list");
            setModalLoading(false);
            return;
          }
          
          // Delete products
          const promises = validProductIds.map((productId) =>
            api.delete(`/shopproducts/${productId}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
          );
          
          await Promise.all(promises);
          
          // Update local state
          setShopProducts((prev) => {
            const next = prev.filter((p) => !validProductIds.includes(p.id));
            shopProductsRef.current = next;
            return next;
          });
          setSelectedRowKeys((prev) => prev.filter((key) => !validProductIds.includes(key)));
          
          message.success(`Removed ${validProductIds.length} product(s) successfully`);
          setBulkRemoveModalVisible(false);
          setBulkActionsModalVisible(false);
          bulkRemoveForm.resetFields();
          
        } catch (error) {
          console.error('Error in bulk remove from file:', error);
          message.error(`Failed to process bulk remove: ${error.message}`);
        } finally {
          setModalLoading(false);
        }
      };
      
      reader.readAsArrayBuffer(file);
      
    } catch (error) {
      console.error('Error validating bulk remove form:', error);
      message.error(`Failed to validate bulk remove form: ${error.message}`);
    }
  }, [bulkRemoveForm, shopProducts, token]);

  // Fetch segments on demand
  const fetchSegmentsOnDemand = useCallback(async (warehouseId) => {
    if (!warehouseId || !token) return;
    
    setModalLoading(true);
    setAvailableQuantity(null);
    
    try {
      const response = await api.get(`/warehousesegments/warehouse/${warehouseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const segmentData = Array.isArray(response.data.warehouseSegments)
        ? response.data.warehouseSegments
        : Array.isArray(response.data.segments)
        ? response.data.segments
        : Array.isArray(response.data)
        ? response.data
        : [];
      
      setSegments(prev => ({ ...prev, [warehouseId]: segmentData }));
      return segmentData;
    } catch (error) {
      console.error('Error fetching segments:', error);
      message.error(`Failed to load segments: ${error.message}`);
      return [];
    } finally {
      setModalLoading(false);
    }
  }, [token]);

  // Search and filter handlers
  const handleSearchChange = useCallback((e) => {
    setBarcodeFilterList(null);
    setSearchTerm(e.target.value || "");
  }, []);

  const handleFilterChange = useCallback((value) => {
    setBarcodeFilterList(null);
    setFilterType(value || "all");
  }, []);

  // Edit handlers
  const handleEdit = useCallback(() => setEditing(true), []);
  
  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const updatedData = { ...shop, ...values };
      
      await api.put(`/shops/${shopId}`, updatedData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setShop(updatedData);
      message.success("Shop updated successfully");
      setEditing(false);
    } catch (error) {
      console.error('Error saving shop:', error);
      message.error(`Failed to update shop: ${error.message}`);
    }
  }, [form, shop, shopId, token]);

  const handleCancel = useCallback(() => {
    setEditing(false);
    if (shop) form.setFieldsValue(shop);
  }, [form, shop]);

  // Edit product handler
  const handleEditProduct = useCallback((record) => {
    setSelectedProduct(record);
    const product = productDetails[record?.productId] || {};
    
    editProductForm.setFieldsValue({
      name: product.name || "",
      barCode: product.barCode || "",
      type: product.type || "",
      description: product.description || "",
      brand: product.brand || "",
      ingredients: product.ingredients || "",
      baseUnitOfMeasure: product.baseUnitOfMeasure || "Unit",
      grossWeight: product.grossWeight || 0,
      netWeight: product.netWeight || 0,
      generalPrice: product.generalPrice || 0,
      applyGeneralPriceToShopProducts: product.applyGeneralPriceToShopProducts,
      active: product.active ?? true,
      imageFilePath: product.images?.[0]?.filePath || "",
      imageTitle: product.images?.[0]?.title || "",
      imageDescription: product.images?.[0]?.description || "",
    });
    
    setEditProductModalVisible(true);
  }, [editProductForm, productDetails]);

  // Product price handler
  const handleProductPrice = useCallback((record) => {
    setSelectedProduct(record);
    const price = record?.currentRetailShopProductPriceToApply;
    
    if (price) {
      priceForm.setFieldsValue({
        price: price.price,
        purpose: price.purpose,
        targetCustomer: price.targetCustomer,
        effectiveDate: moment(price.effectiveDate),
        endDate: moment(price.endDate),
        active: price.active,
      });
      setSelectedPrice(price);
    } else {
      priceForm.resetFields();
      setSelectedPrice(null);
    }
    
    setPriceModalVisible(true);
  }, [priceForm]);

  // View handler
  const handleView = useCallback((record) => {
    if (!record?.productId) {
      message.error("Unable to view product: No product ID found");
      return;
    }
    
    const price = record.currentRetailShopProductPriceToApply;
    navigate("/shopproduct/details", {
      state: {
        productId: record.productId,
        price: price ? price.price : null,
      },
    });
  }, [navigate]);

  // Export to Excel handler
  const handleExportToExcel = useCallback(() => {
    if (!selectedRowKeys.length) {
      message.warning("Please select at least one product to export");
      return;
    }
    
    try {
      const data = filteredShopProducts
        .filter((product) => selectedRowKeys.includes(product.id))
        .map((product) => {
          const details = productDetails[product.productId] || {};
          return {
            "Product ID": product.productId || "N/A",
            "Product Name": details.name || "N/A",
            "Bar Code": details.barCode || "N/A",
            "Price": product.currentRetailShopProductPriceToApply?.price?.toFixed(2) || "N/A",
            "Qty On Hand": product.totalQuantityOnHandAtShopDisplays?.toFixed(2) || "0.00",
          };
        });
      
      if (!data.length) {
        message.error("No valid data to export for selected products");
        return;
      }
      
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Shop Products");
      XLSX.writeFile(workbook, "shop_products.xlsx");
      message.success("Products exported successfully");
      setBulkActionsModalVisible(false);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      message.error(`Failed to export to Excel: ${error.message}`);
    }
  }, [selectedRowKeys, filteredShopProducts, productDetails]);

  // Bulk search handler
  const handleBulkSearch = useCallback(async () => {
    try {
      const values = await bulkSearchForm.validateFields();
      const file = values.file.file;
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(sheet);
          
          if (!jsonData.length) {
            message.error("Excel file is empty");
            return;
          }
          
          const requiredFields = ["BarCode"];
          const missingFields = requiredFields.filter(
            (field) => !Object.keys(jsonData[0]).includes(field)
          );
          
          if (missingFields.length) {
            message.error(`Missing required field in Excel: ${missingFields.join(", ")}`);
            return;
          }
          
          const barcodes = jsonData
            .map((row) => row["BarCode"]?.toString().trim())
            .filter((barcode) => barcode);
          
          if (!barcodes.length) {
            message.error("No valid barcodes found in the Excel file");
            return;
          }
          
          const allow = new Set(barcodes);
          const matched = shopProducts.filter((product) => {
            const barCode = getProductInfo(product, productDetails).barCode?.toString().trim();
            return barCode && allow.has(barCode);
          });

          if (!matched.length) {
            message.warning("No products found matching the provided barcodes");
            setBarcodeFilterList(null);
          } else {
            setBarcodeFilterList(barcodes);
            setSearchTerm("");
            setFilterType("all");
            setCurrentPage(1);
            message.success(`Found ${matched.length} product(s) matching the provided barcodes`);
          }
          
          setBulkSearchModalVisible(false);
          setBulkActionsModalVisible(false);
          bulkSearchForm.resetFields();
          
        } catch (error) {
          console.error('Error in bulk search:', error);
          message.error(`Failed to process bulk search: ${error.message}`);
        }
      };
      
      reader.readAsArrayBuffer(file);
      
    } catch (error) {
      console.error('Error validating bulk search form:', error);
      message.error(`Failed to validate bulk search form: ${error.message}`);
    }
  }, [bulkSearchForm, shopProducts, productDetails]);

  // Other handlers
  const showPriceDetails = useCallback((price) => {
    setSelectedPrice(price);
    setPriceDetailsVisible(true);
  }, []);

  const showWarehouseDetails = useCallback((warehouse) => {
    setSelectedWarehouse(warehouse);
    setWarehouseDetailsVisible(true);
  }, []);

  const handleOpenAddQuantityModal = useCallback((record) => {
    setSelectedProduct(record);
    setAddQuantityModalVisible(true);
    setAvailableQuantity(null);
    addQuantityForm.resetFields();
  }, [addQuantityForm]);

  const toggleShowFullName = useCallback((productId) => {
    setShowFullName(prev => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  }, []);

  // Table columns
  const shopProductColumns = useMemo(
    () => [
      {
        title: (
          <Checkbox
            checked={
              filteredShopProducts.length > 0 &&
              selectedRowKeys.length === filteredShopProducts.length
            }
            indeterminate={
              selectedRowKeys.length > 0 &&
              selectedRowKeys.length < filteredShopProducts.length
            }
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedRowKeys(filteredShopProducts.map((p) => p.id));
              } else {
                setSelectedRowKeys([]);
              }
            }}
          >
            Select All
          </Checkbox>
        ),
        key: "selection",
        width: 50,
        fixed: "left",
        render: (_, record) => (
          <Checkbox
            checked={selectedRowKeys.includes(record.id)}
            onChange={(e) => {
              const checked = e.target.checked;
              setSelectedRowKeys((prev) =>
                checked ? [...prev, record.id] : prev.filter((key) => key !== record.id)
              );
            }}
          />
        ),
      },
      {
        title: "Image",
        key: "image",
        width: 80,
        render: (_, record) => {
          const product = productDetails[record?.productId] || {};
          return product.images?.length > 0 ? (
            <Image
              src={product.images[0].filePath}
              alt={product.images[0].title || "product"}
              width={50}
              height={50}
              style={{ objectFit: "cover" }}
              preview={false}
              placeholder={<div style={{ width: 50, height: 50, background: "#f0f0f0" }} />}
            />
          ) : (
            <div
              style={{
                width: 50,
                height: 50,
                lineHeight: "50px",
                textAlign: "center",
                background: "#f0f0f0",
              }}
            >
              No Image
            </div>
          );
        },
      },
      {
        title: "Bar Code",
        key: "barCode",
        width: 150,
        render: (_, record) => productDetails[record?.productId]?.barCode || "N/A",
      },
      {
        title: "Product Name",
        key: "name",
        width: 300,
        render: (_, record) => {
          const productId = record?.productId;
          const text = productDetails[productId]?.name || "N/A";
          const isTruncated = text.length > 35 && !showFullName[productId];
          return (
            <Tooltip title={text}>
              <span>
                {isTruncated ? `${text.slice(0, 35)}...` : text}
                {text.length > 35 && (
                  <a
                    style={{ marginLeft: 8, color: "#1890ff" }}
                    onClick={() => toggleShowFullName(productId)}
                  >
                    {showFullName[productId] ? "Less" : "More"}
                  </a>
                )}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: "Product Price",
        key: "price",
        width: 120,
        render: (_, record) => {
          const shopPrice = record?.currentRetailShopProductPriceToApply;
          const generalPrice = productDetails[record?.productId]?.generalPrice;
          const priceToDisplay = shopPrice?.price || generalPrice;
          return priceToDisplay ? (
            <a
              onClick={() => showPriceDetails(shopPrice || { price: generalPrice })}
              style={{ color: "#1890ff" }}
            >
              {priceToDisplay.toFixed(2)}
            </a>
          ) : (
            "N/A"
          );
        },
      },
      {
        title: "Tax Price",
        key: "finalPricePerUnit",
        width: 120,
        render: (_, record) => {
          const recalculatedPrice = record?.recalculatedPricePerUnit;
          const finalPricePerUnit = recalculatedPrice?.finalPricePerUnit;
          return finalPricePerUnit ? (
            <a
              style={{ color: "#1890ff" }}
            >
              {finalPricePerUnit.toFixed(2)}
            </a>
          ) : (
            "N/A"
          );
        },
      },
      {
        title: "Qty On Hand",
        key: "quantityOnHand",
        width: 120,
        render: (_, record) => (record?.totalQuantityOnHandAtShopDisplays || 0).toFixed(2),
      },
      {
        title: "Actions",
        key: "actions",
        width: 100,
        fixed: "right",
        render: (_, record) => (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="edit" onClick={() => handleEditProduct(record)}>
                  <EditOutlined /> Edit Product
                </Menu.Item>
                <Menu.Item key="archive" onClick={() => handleDelete(record.id)}>
                  <DeleteRowOutlined /> Archive Product
                </Menu.Item>
                <Menu.Item key="price" onClick={() => handleProductPrice(record)}>
                  <DollarCircleFilled />{" "}
                  {record.currentRetailShopProductPriceToApply
                    ? "Update Price"
                    : "Product Price"}
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
          >
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        ),
      },
    ],
    [
      selectedRowKeys,
      filteredShopProducts,
      productDetails,
      showFullName,
      toggleShowFullName,
      handleDelete,
      handleEditProduct,
      handleProductPrice,
      showPriceDetails,
    ]
  );

  const shopDisplayColumns = useMemo(
    () => [
      { title: "Display Code", dataIndex: "code", key: "code", width: 150, render: (code) => code || "N/A" },
      { title: "Name", dataIndex: "name", key: "name", width: 200, render: (name) => name || "N/A" },
      { title: "Display Type", dataIndex: "displayType", key: "displayType", width: 150, render: (type) => type || "N/A" },
      {
        title: "Capacity",
        dataIndex: "capacity",
        key: "capacity",
        width: 150,
        render: (capacity, record) => `${capacity || 0} ${record?.capacityUnit || ""}`,
      },
    ],
    []
  );

  const shopWarehouseColumns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: ["warehouse", "name"],
        key: "name",
        width: 200,
        render: (name, record) => (
          <a onClick={() => showWarehouseDetails(record)} style={{ color: "#1890ff" }}>
            {name || "N/A"}
          </a>
        ),
      },
      { title: "Code", dataIndex: ["warehouse", "code"], key: "code", width: 150, render: (code) => code || "N/A" },
      { title: "Phone", dataIndex: ["warehouse", "phone"], key: "phone", width: 150, render: (phone) => phone || "N/A" },
    ],
    [showWarehouseDetails]
  );

  // CSS styles
  const tableStyles = `
    .ant-table { 
      table-layout: fixed !important; 
      border-collapse: collapse !important; 
    } 
    .ant-table-thead > tr > th { 
      white-space: nowrap; 
      overflow: hidden; 
      text-overflow: ellipsis; 
      background: #fafafa; 
      padding: 8px !important; 
      font-weight: 500; 
    } 
    .ant-table-tbody > tr > td { 
      white-space: normal; 
      word-break: break-word; 
      padding: 8px !important; 
      min-height: 50px; 
    } 
    .ant-table-container { 
      overflow-x: auto; 
      max-width: 100%; 
    } 
    .ant-table-body { 
      min-width: 990px !important; 
    }
  `;

  if (loading && !shop) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Loading shop data..." />
      </div>
    );
  }

  if (!shop) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h2>No shop data found</h2>
        <Button type="primary" onClick={() => navigate("/shops")}>
          Go Back to Shops
        </Button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div style={{ padding: "20px", maxWidth: "2000px", margin: "0 auto" }}>
        <style>{tableStyles}</style>
        
        <Card style={{ marginBottom: 20 }}>
          <Button
            type="primary"
            style={{ marginRight: "10px" }}
            onClick={() => navigate("/shop/till", { state: { shopId } })}
          >
            TILL MANAGEMENT
          </Button>
          <Button
            type="primary"
            style={{ marginRight: "10px" }}
            onClick={() => navigate("/shop/salesTransactions", { state: { shopId } })}
          >
            SALES TRANSACTIONS
          </Button>
          <Button
            type="primary"
            style={{ marginRight: "10px" }}
            onClick={() => navigate("/shop/ReportTransactions", { state: { shopId } })}
          >
            REPORTS & ANALYTICS
          </Button>
          {/* <Button
            type="primary"
            style={{ marginRight: "10px" }}
            onClick={() => navigate("/shop/ShopDisplayInventories", { state: { shopId } })}
          >
            SHOP INVENTORIES
          </Button> */}
          <Button
            type="primary"
            style={{ marginRight: "10px" }}
            onClick={() => navigate("/shop/ShopDisplayLocations", { state: { shopId } })}
          >
            PRODUCT LOCATIONS
          </Button>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={20} md={16} lg={16}>
            <Card
              title={
                <Title level={3} style={{ margin: 0, color: '#1a3c34' }}>
                  {businessName || 'Shop'} - Details
                </Title>
              }
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(145deg, #ffffff, #f7fafc)',
                overflow: 'hidden',
              }}
              headStyle={{
                borderBottom: '1px solid #e8ecef',
                padding: '16px 24px',
                background: '#fafafa',
              }}
            >
              <Row gutter={[24, 24]} align="middle" style={{ padding: '24px' }}>
                <Col xs={24} sm={8} md={6}>
                  <Image
                    src={shop.logo || '/assets/images/shop.jpg'}
                    alt="Shop Logo"
                    width="100%"
                    height={200}
                    style={{
                      borderRadius: '8px',
                      objectFit: 'cover',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    }}
                    preview
                    fallback="/assets/images/shop.jpg"
                    placeholder={
                      <div
                        style={{
                          width: '100%',
                          height: 150,
                          background: '#f0f0f0',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Text type="secondary">Loading...</Text>
                      </div>
                    }
                  />
                </Col>
                <Col xs={24} sm={16} md={18}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Text strong style={{ fontSize: '16px', color: '#6c757d' }}>
                        Name
                      </Text>
                      <Title level={4} style={{ margin: '8px 0', color: '#1a3c34' }}>
                        {shop.name || 'N/A'}
                      </Title>
                    </Col>
                    <Col xs={24} md={12}>
                      <Text strong style={{ fontSize: '16px', color: '#6c757d' }}>
                        Location
                      </Text>
                      <Title level={4} style={{ margin: '8px 0', color: '#1a3c34' }}>
                        {shop.locationAddress || 'N/A'}
                      </Title>
                    </Col>
                  </Row>
                </Col>
              </Row>
              
              <Card
                title="Shop Information"
                style={{
                  marginTop: '24px',
                  borderRadius: '8px',
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
                headStyle={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1a3c34',
                  borderBottom: '1px solid #e8ecef',
                  padding: '12px 24px',
                }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong style={{ display: 'block', color: '#6c757d', marginBottom: '8px' }}>
                        Name
                      </Text>
                      {editing ? (
                        <Form.Item
                          name="name"
                          rules={[{ required: true, message: 'Please enter shop name' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            placeholder="Enter shop name"
                            style={{ borderRadius: '6px', padding: '8px' }}
                          />
                        </Form.Item>
                      ) : (
                        <Text>{shop.name || 'N/A'}</Text>
                      )}
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong style={{ display: 'block', color: '#6c757d', marginBottom: '8px' }}>
                        Code
                      </Text>
                      <Text>{shop.code || 'N/A'}</Text>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong style={{ display: 'block', color: '#6c757d', marginBottom: '8px' }}>
                        Email
                      </Text>
                      {editing ? (
                        <Form.Item
                          name="email"
                          rules={[{ type: 'email', message: 'Enter a valid email' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            placeholder="Enter email"
                            style={{ borderRadius: '6px', padding: '8px' }}
                          />
                        </Form.Item>
                      ) : (
                        <Text>{shop.email || 'N/A'}</Text>
                      )}
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong style={{ display: 'block', color: '#6c757d', marginBottom: '8px' }}>
                        Telephone
                      </Text>
                      {editing ? (
                        <Form.Item name="telephone" style={{ marginBottom: 0 }}>
                          <Input
                            placeholder="Enter telephone"
                            style={{ borderRadius: '6px', padding: '8px' }}
                          />
                        </Form.Item>
                      ) : (
                        <Text>{shop.telephone || 'N/A'}</Text>
                      )}
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text strong style={{ display: 'block', color: '#6c757d', marginBottom: '8px' }}>
                        Location Address
                      </Text>
                      {editing ? (
                        <Form.Item name="locationAddress" style={{ marginBottom: 0 }}>
                          <Input
                            placeholder="Enter location address"
                            style={{ borderRadius: '6px', padding: '8px' }}
                          />
                        </Form.Item>
                      ) : (
                        <Text>{shop.locationAddress || 'N/A'}</Text>
                      )}
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text strong style={{ display: 'block', color: '#6c757d', marginBottom: '8px' }}>
                        Description
                      </Text>
                      {editing ? (
                        <Form.Item name="descriptionOfService" style={{ marginBottom: 0 }}>
                          <Input.TextArea
                            placeholder="Enter description"
                            rows={3}
                            style={{ borderRadius: '6px', padding: '8px' }}
                          />
                        </Form.Item>
                      ) : (
                        <Text>{shop.descriptionOfService || 'N/A'}</Text>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card>
              
              {editing && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <Button
                    onClick={handleCancel}
                    style={{
                      borderRadius: '8px',
                      marginRight: '12px',
                      borderColor: '#d9d9d9',
                      color: '#6c757d',
                    }}
                    icon={<CloseOutlined />}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    style={{
                      borderRadius: '8px',
                      background: 'linear-gradient(90deg, #1a3c34, #2e6b5b)',
                      border: 'none',
                    }}
                    icon={<SaveOutlined />}
                  >
                    Save Changes
                  </Button>
                </div>
              )}
            </Card>
          </Col>
          
          <Col xs={24} md={8}>
            <Card title="Shop Warehouse Info" style={{ marginTop: "10px" }}>
              <Table
                columns={shopWarehouseColumns}
                dataSource={shopWarehouses}
                rowKey="id"
                pagination={{ pageSize: 2 }}
                size="small"
                bordered
                tableLayout="fixed"
                scroll={{ x: 500 }}
              />
            </Card>
            <Card title="Shop Displays" style={{ borderRadius: "8px", marginTop: "10px" }}>
              <Table
                columns={shopDisplayColumns}
                dataSource={shopDisplays}
                rowKey="id"
                pagination={{ pageSize: 2 }}
                size="small"
                bordered
                tableLayout="fixed"
                scroll={{ x: 650 }}
              />
            </Card>
          </Col>
        </Row>

        <Row style={{ marginTop: "20px" }}>
          <Col span={24}>
            <Card 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Shop Products</span>
                  <Button
                    type="primary"
                    icon={<EllipsisOutlined />}
                    onClick={() => setBulkActionsModalVisible(true)}
                    style={{ borderRadius: "8px" }}
                  >
                    Bulk Actions
                  </Button>
                </div>
              }
              style={{ borderRadius: "8px" }}
            >
              <div style={{ marginBottom: "16px", display: "flex", gap: "10px", alignItems: "center" }}>
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="Search by product name, code, or barcode"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={{ width: "300px" }}
                  allowClear
                />
                <Select
                  value={filterType}
                  onChange={handleFilterChange}
                  style={{ width: "200px" }}
                  placeholder="Filter Products"
                >
                  <Option value="all">All Products</Option>
                  <Option value="withQuantity">With Quantity</Option>
                  <Option value="withoutQuantity">Without Quantity</Option>
                  <Option value="withBarcode">With Barcode</Option>
                  <Option value="withoutBarcode">Without Barcode</Option>
                  <Option value="withoutImages">Without Images</Option>
                  <Option value="withPrice">With Price</Option>
                  <Option value="withoutPrice">Without Price</Option>
                </Select>
              </div>
              
              <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
                {isLoadingCatalog && (
                  <Progress
                    percent={catalogLoadProgress}
                    status="active"
                    showInfo
                    style={{ marginBottom: 8 }}
                  />
                )}
                  <Table
  columns={shopProductColumns}
  dataSource={paginatedShopProducts}
  rowKey="id"
  pagination={{
    current: currentPage,
    pageSize,
    total: filteredShopProducts.length,
    pageSizeOptions: ["10", "20", "50", "100"],
    showSizeChanger: true,
    onChange: (page, size) => {
      setCurrentPage(page);
      setPageSize(size);
    },
  }}
  size="small"
  bordered
  loading={productsLoading}
  scroll={{ x: 990 }}
  tableLayout="fixed"
/>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Bulk Actions Modal */}
        <Modal
          title="Bulk Actions"
          open={bulkActionsModalVisible}
          onCancel={() => setBulkActionsModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setBulkActionsModalVisible(false)}>
              Close
            </Button>,
          ]}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => setBulkSearchModalVisible(true)}
              style={{ borderRadius: "8px" }}
            >
              Bulk Search by Barcode
            </Button>
            <Button
              type="primary"
              icon={<DeleteOutlined />}
              onClick={handleBulkRemove}
              disabled={!selectedRowKeys.length}
              style={{ borderRadius: "8px" }}
            >
              Product Bulk Remove
            </Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setBulkPriceModalVisible(true)}
              style={{ borderRadius: "8px" }}
            >
              Bulk Update Prices
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportToExcel}
              disabled={!selectedRowKeys.length}
              style={{ borderRadius: "8px" }}
            >
              Export to Excel
            </Button>
          </div>
        </Modal>

        {/* Price Modal */}
        <Modal
          title={selectedPrice ? "Update Product Price" : "Add Product Price"}
          open={priceModalVisible}
          onOk={handlePriceSubmit}
          maskClosable={false}
          onCancel={() => {
            setPriceModalVisible(false);
            priceForm.resetFields();
            setSelectedPrice(null);
          }}
          okText="Save"
          cancelText="Cancel"
          confirmLoading={modalLoading}
        >
          <Form form={priceForm} layout="vertical">
            <Form.Item
              name="price"
              label="Price"
              rules={[
                { required: true, message: "Please enter the price" },
                {
                  validator: (_, value) =>
                    value && (!/^\d*\.?\d+$/.test(value) || parseFloat(value) <= 0)
                      ? Promise.reject(new Error("Price must be a positive number"))
                      : Promise.resolve(),
                },
              ]}
            >
              <Input
                onKeyPress={(e) => {
                  const charCode = e.charCode;
                  if (charCode !== 46 && (charCode < 48 || charCode > 57)) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter price (e.g., 123.45)"
              />
            </Form.Item>
            <Form.Item
              name="purpose"
              label="Purpose"
              rules={[{ required: true, message: "Please select a purpose" }]}
            >
              <Select placeholder="Select purpose">
                {(pricePurposes || []).map((purpose) => (
                  <Option key={purpose} value={purpose}>
                    {purpose}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="targetCustomer"
              label="Target Customer"
              rules={[{ required: true, message: "Please select a target customer" }]}
            >
              <Select placeholder="Select target customer">
                {(targetCustomers || []).map((customer) => (
                  <Option key={customer} value={customer}>
                    {customer}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="effectiveDate"
              label="Effective Date"
              rules={[{ required: true, message: "Please select the effective date" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="endDate"
              label="End Date"
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <div style={{ marginBottom: 16, color: '#faad14' }}>
              ⚠️ Caution: When the product's end date expires, it will be removed from the POS. Review and update end dates as needed.
            </div>
            <Form.Item name="active" label="Active" valuePropName="checked" initialValue={true}>
              <Checkbox />
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit Product Modal */}
        <Modal
          title="Edit Product"
          open={editProductModalVisible}
          onOk={handleEditProductSubmit}
          maskClosable={false}
          onCancel={() => {
            setEditProductModalVisible(false);
            editProductForm.resetFields();
            setSelectedProduct(null);
          }}
          okText="Save"
          cancelText="Cancel"
          okButtonProps={{ loading: modalLoading }}
          width={800}
        >
          <Spin spinning={modalLoading}>
            <Form form={editProductForm} layout="vertical">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="Product Name"
                    rules={[{ required: true, message: "Please enter the product name" }]}
                  >
                    <Input placeholder="Enter product name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="barCode"
                    label="Bar Code"
                  >
                    <Input placeholder="Enter bar code" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="type"
                    label="Type"
                    rules={[{ required: true, message: "Please enter the product type" }]}
                  >
                    <Input placeholder="Enter product type" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="brand"
                    label="Brand"
                    rules={[{ required: false }]}
                  >
                    <Input placeholder="Enter brand" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="description"
                    label="Description"
                    rules={[{ required: false }]}
                  >
                    <Input.TextArea rows={4} placeholder="Enter product description" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="ingredients"
                    label="Ingredients"
                    rules={[{ required: false }]}
                  >
                    <Input.TextArea rows={4} placeholder="Enter ingredients" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="baseUnitOfMeasure"
                    label="Unit of Measure"
                    rules={[{ required: true, message: "Please select a unit of measure" }]}
                  >
                    <Select placeholder="Select unit of measure">
                      <Option value="Unit">Unit</Option>
                      <Option value="Piece">Piece</Option>
                      <Option value="Box">Box</Option>
                      <Option value="Kilogram">Kilogram</Option>
                      <Option value="Liter">Liter</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="grossWeight"
                    label="Gross Weight"
                    rules={[
                      { required: true, message: "Please enter the gross weight" },
                      {
                        validator: (_, value) =>
                          value && parseFloat(value) <= 0
                            ? Promise.reject(new Error("Gross weight must be a positive number"))
                            : Promise.resolve(),
                      },
                    ]}
                  >
                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} placeholder="Enter gross weight" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="netWeight"
                    label="Net Weight"
                    rules={[
                      { required: true, message: "Please enter the net weight" },
                      {
                        validator: (_, value) =>
                          value && parseFloat(value) <= 0
                            ? Promise.reject(new Error("Net weight must be a positive number"))
                            : Promise.resolve(),
                      },
                    ]}
                  >
                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} placeholder="Enter net weight" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="generalPrice"
                    label="General Price"
                    rules={[
                      { required: true, message: "Please enter the general price" },
                      {
                        validator: (_, value) =>
                          value && parseFloat(value) <= 0
                            ? Promise.reject(new Error("General price must be a positive number"))
                            : Promise.resolve(),
                      },
                    ]}
                  >
                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} placeholder="Enter general price" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="active"
                    label="Active"
                    valuePropName="checked"
                    initialValue={true}
                  >
                    <Checkbox />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="imageFilePath"
                    label="Image File Path"
                    rules={[{ required: false }]}
                  >
                    <Input placeholder="Enter image file path (URL)" />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(prev, curr) => prev.imageFilePath !== curr.imageFilePath}>
                    {({ getFieldValue }) => {
                      const url = getFieldValue("imageFilePath");
                      return url ? (
                        <Image
                          src={url}
                          alt="Preview"
                          style={{ maxWidth: "150px", marginTop: "10px" }}
                        />
                      ) : null;
                    }}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="imageTitle"
                    label="Image Title"
                    rules={[{ required: false }]}
                  >
                    <Input placeholder="Enter image title" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="imageDescription"
                    label="Image Description"
                    rules={[{ required: false }]}
                  >
                    <Input placeholder="Enter image description" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Modal>

        {/* Price Details Modal */}
        <Modal
          title="Product Price Details"
          open={priceDetailsVisible}
          onOk={() => setPriceDetailsVisible(false)}
          onCancel={() => setPriceDetailsVisible(false)}
          okText="Close"
          cancelButtonProps={{ style: { display: "none" } }}
        >
          {selectedPrice && (
            <div>
              <p><strong>Price:</strong> {selectedPrice.price?.toFixed(2) || "N/A"}</p>
              <p><strong>Purpose:</strong> {selectedPrice.purpose || "N/A"}</p>
              <p><strong>Target Customer:</strong> {selectedPrice.targetCustomer || "N/A"}</p>
              <p><strong>Effective Date:</strong> {selectedPrice.effectiveDate ? new Date(selectedPrice.effectiveDate).toLocaleDateString() : "N/A"}</p>
              <p><strong>End Date:</strong> {selectedPrice.endDate ? new Date(selectedPrice.endDate).toLocaleDateString() : "N/A"}</p>
              <p><strong>Active:</strong> {selectedPrice.active ? "Yes" : "No"}</p>
              <p><strong>Created Date:</strong> {selectedPrice.createdDate ? new Date(selectedPrice.createdDate).toLocaleDateString() : "N/A"}</p>
              <p><strong>Modified Date:</strong> {selectedPrice.modifiedDate ? new Date(selectedPrice.modifiedDate).toLocaleDateString() : "N/A"}</p>
            </div>
          )}
        </Modal>

        {/* Warehouse Details Modal */}
        <Modal
          title="Warehouse Details"
          open={warehouseDetailsVisible}
          onOk={() => setWarehouseDetailsVisible(false)}
          onCancel={() => setWarehouseDetailsVisible(false)}
          okText="Close"
          cancelButtonProps={{ style: { display: "none" } }}
        >
          {selectedWarehouse && (
            <div>
              <p><strong>Name:</strong> {selectedWarehouse.warehouse?.name || "N/A"}</p>
              <p><strong>Code:</strong> {selectedWarehouse.warehouse?.code || "N/A"}</p>
              <p><strong>Address:</strong> {selectedWarehouse.warehouse?.address || "N/A"}</p>
              <p><strong>Description:</strong> {selectedWarehouse.description || "N/A"}</p>
              <p><strong>Phone:</strong> {selectedWarehouse.warehouse?.phone || "N/A"}</p>
              <p><strong>Email:</strong> {selectedWarehouse.warehouse?.email || "N/A"}</p>
              <p><strong>Manager Name:</strong> {selectedWarehouse.warehouse?.managerName || "N/A"}</p>
              <p><strong>Manager Phone:</strong> {selectedWarehouse.warehouse?.managerPhone || "N/A"}</p>
              <p><strong>City:</strong> {selectedWarehouse.warehouse?.city || "N/A"}</p>
              <p><strong>Postal Code:</strong> {selectedWarehouse.warehouse?.postalCode || "N/A"}</p>
              <p><strong>Country:</strong> {selectedWarehouse.warehouse?.country || "N/A"}</p>
              <p><strong>GPS Location:</strong> {selectedWarehouse.warehouse?.gpsLocation || "N/A"}</p>
              <p><strong>Capacity:</strong> {selectedWarehouse.warehouse?.capacity ? `${selectedWarehouse.warehouse.capacity} ${selectedWarehouse.warehouse.capacityUnit || ""}` : "N/A"}</p>
              <p><strong>Product Count:</strong> {selectedWarehouse.warehouse?.productCount || "0"}</p>
              <p><strong>Created Date:</strong> {selectedWarehouse.createdDate ? new Date(selectedWarehouse.createdDate).toLocaleDateString() : "N/A"}</p>
              <p><strong>Modified Date:</strong> {selectedWarehouse.modifiedDate ? new Date(selectedWarehouse.modifiedDate).toLocaleDateString() : "N/A"}</p>
            </div>
          )}
        </Modal>

        {/* Add Quantity Modal */}
        <Modal
          title={`Add Quantity for ${selectedProduct?.product?.name || "Product"}`}
          open={addQuantityModalVisible}
          onOk={handleAddQuantity}
          onCancel={() => {
            setAddQuantityModalVisible(false);
            addQuantityForm.resetFields();
            setSelectedProduct(null);
            setAvailableQuantity(null);
          }}
          okText="Submit"
          cancelText="Cancel"
          okButtonProps={{ loading: modalLoading }}
          width={600}
        >
          <Spin spinning={modalLoading}>
            <Form form={addQuantityForm} layout="vertical">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  {selectedProduct?.product?.images?.[0]?.filePath && (
                    <Image
                      src={selectedProduct.product.images[0].filePath}
                      alt={selectedProduct.product.name || "Product"}
                      width={50}
                      height={50}
                      style={{ objectFit: "cover", marginBottom: 16 }}
                      preview={false}
                      placeholder={<div style={{ width: 50, height: 50, background: "#f0f0f0" }} />}
                    />
                  )}
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="warehouseId"
                    label="Warehouse"
                    rules={[{ required: true, message: "Please select a warehouse" }]}
                  >
                    <Select
                      placeholder={warehouses.length ? "Select Warehouse" : "No warehouses available"}
                      style={{ width: "100%" }}
                      showSearch
                      filterOption={(input, option) =>
                        option.children?.toLowerCase().includes(input.toLowerCase())
                      }
                      disabled={!warehouses.length}
                      onChange={(value) => {
                        fetchSegmentsOnDemand(value);
                        addQuantityForm.setFieldsValue({ sourceSegmentId: undefined, shopDisplayId: undefined });
                        setAvailableQuantity(null);
                      }}
                    >
                      {(warehouses || []).map((warehouse) => (
                        <Option key={warehouse.id} value={warehouse.id}>
                          {warehouse.name || warehouse.id}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="sourceSegmentId"
                    label="Segment"
                    rules={[{ required: true, message: "Please select a segment" }]}
                  >
                    <Select
                      placeholder={
                        addQuantityForm.getFieldValue("warehouseId") &&
                        segments[addQuantityForm.getFieldValue("warehouseId")]?.length
                          ? "Select Segment"
                          : "No segments available"
                      }
                      style={{ width: "100%" }}
                      showSearch
                      filterOption={(input, option) =>
                        option.children?.toLowerCase().includes(input.toLowerCase())
                      }
                      disabled={
                        !addQuantityForm.getFieldValue("warehouseId") ||
                        !segments[addQuantityForm.getFieldValue("warehouseId")]?.length
                      }
                      onChange={(value) => {
                        const warehouseId = addQuantityForm.getFieldValue("warehouseId");
                        if (warehouseId && value && selectedProduct?.productId) {
                          const segment = segments[warehouseId]?.find((seg) => seg.id === value);
                          const inventoryItem = segment?.inventory?.find(
                            (item) => item.productId === selectedProduct.productId
                          );
                          setAvailableQuantity(inventoryItem?.quantity || 0);
                        } else {
                          setAvailableQuantity(null);
                        }
                      }}
                    >
                      {(segments[addQuantityForm.getFieldValue("warehouseId")] || []).map((segment) => (
                        <Option key={segment.id} value={segment.id}>
                          {segment.name || segment.id}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <div style={{ marginBottom: 16 }}>
                    <strong>Available Quantity: </strong>
                    {availableQuantity !== null ? availableQuantity.toFixed(2) : "N/A"}
                  </div>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="shopDisplayId"
                    label="Shop Display"
                    rules={[{ required: true, message: "Please select a shop display" }]}
                  >
                    <Select
                      placeholder={shopDisplays.length ? "Select Shop Display" : "No shop displays available"}
                      style={{ width: "100%" }}
                      showSearch
                      filterOption={(input, option) =>
                        option.children?.toLowerCase().includes(input.toLowerCase())
                      }
                      disabled={!shopDisplays.length}
                    >
                      {(shopDisplays || []).map((display) => (
                        <Option key={display.id} value={display.id}>
                          {display.name || display.id}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="quantity"
                    label="Quantity"
                    rules={[
                      { required: true, message: "Please enter the quantity" },
                      { type: "number", min: 1, message: "Quantity must be at least 1" },
                      {
                        validator: (_, value) =>
                          value && availableQuantity !== null && value > availableQuantity
                            ? Promise.reject(
                                new Error(`Quantity cannot exceed available quantity (${availableQuantity.toFixed(2)})`)
                              )
                            : Promise.resolve(),
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      step={1}
                      style={{ width: "100%" }}
                      placeholder="Enter quantity"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Modal>

        {/* Bulk Price Update Modal */}
        <Modal
          title="Bulk Update Prices"
          open={bulkPriceModalVisible}
          onOk={handleBulkPriceUpdate}
          onCancel={() => {
            setBulkPriceModalVisible(false);
            bulkPriceForm.resetFields();
          }}
          okText="Update"
          cancelText="Cancel"
          okButtonProps={{ loading: modalLoading }}
        >
          <Form form={bulkPriceForm} layout="vertical">
            <Form.Item
              name="file"
              label="Upload Excel File"
              rules={[{ required: true, message: "Please upload an Excel file" }]}
            >
              <Upload
                accept=".xlsx,.xls"
                beforeUpload={() => false}
                maxCount={1}
                style={{ width: "100%" }}
              >
                <Button icon={<UploadOutlined />}>Select Excel File</Button>
              </Upload>
            </Form.Item>
            <div style={{ marginTop: "16px" }}>
              <Text type="secondary">
                Please upload an Excel file with the following columns: Product ID (required), Price (required),
                Purpose, Target Customer, Effective Date, End Date, Active. Ensure Product ID matches existing
                shop products and Price is a positive number.
              </Text>
            </div>
          </Form>
        </Modal>

        {/* Bulk Search Modal */}
        <Modal
          title="Bulk Search by Barcode"
          open={bulkSearchModalVisible}
          onOk={handleBulkSearch}
          onCancel={() => {
            setBulkSearchModalVisible(false);
            bulkSearchForm.resetFields();
          }}
          okText="Search"
          cancelText="Cancel"
          okButtonProps={{ loading: modalLoading }}
        >
          <Form form={bulkSearchForm} layout="vertical">
            <Form.Item
              name="file"
              label="Upload Excel File"
              rules={[{ required: true, message: "Please upload an Excel file" }]}
            >
              <Upload
                accept=".xlsx,.xls"
                beforeUpload={() => false}
                maxCount={1}
                style={{ width: "100%" }}
              >
                <Button icon={<UploadOutlined />}>Select Excel File</Button>
              </Upload>
            </Form.Item>
            <div style={{ marginTop: "16px" }}>
              <Text type="secondary">
                Please upload an Excel file with a "BarCode" column containing the barcodes to search for.
              </Text>
              <div style={{ marginTop: '8px' }}>
                <a
                  href="#"
                  onClick={handleDownload}
                  style={{ color: '#1890ff' }}
                >
                  Download Template
                </a>
              </div>
            </div>
          </Form>
        </Modal>

        {/* Bulk Remove Modal */}
        <Modal
          title="Product Bulk Remove by File"
          open={bulkRemoveModalVisible}
          onOk={handleBulkRemoveFromFile}
          onCancel={() => {
            setBulkRemoveModalVisible(false);
            bulkRemoveForm.resetFields();
          }}
          okText="Remove"
          cancelText="Cancel"
          okButtonProps={{ loading: modalLoading }}
        >
          <Form form={bulkRemoveForm} layout="vertical">
            <Form.Item
              name="file"
              label="Upload Excel File"
              rules={[{ required: true, message: "Please upload an Excel file" }]}
            >
              <Upload
                accept=".xlsx,.xls"
                beforeUpload={() => false}
                maxCount={1}
                style={{ width: "100%" }}
              >
                <Button icon={<UploadOutlined />}>Select Excel File</Button>
              </Upload>
            </Form.Item>
            <div style={{ marginTop: "16px" }}>
              <Text type="secondary">
                Please upload an Excel file with a "Product ID" column containing the IDs of products to remove.
              </Text>
            </div>
          </Form>
        </Modal>
      </div>
    </ErrorBoundary>
  );
}

export default ShopCardDetails;